// src/utils/excelReport.js

import { exportManagerReportZip, exportWipCsv } from "./csvReport";

import {
  exportManagerReportXlsx,
  exportWipFilteredXlsx as exportWipXlsx,
} from "./xlsxReport";

import { showExportChoice } from "./exportChoiceModal";

// If you later want normalization, do it here.
// For now: pass-through (keeps current behavior)
export function normalizeKpiRow(ro) {
  return ro;
}
export function normalizeWipRow(ro) {
  return ro;
}

let exportChoiceBusy = false;

async function chooseKpiExportType() {
  if (exportChoiceBusy) return null;
  exportChoiceBusy = true;

  try {
    return await showExportChoice({
      title: "Export KPI Report",
      subtitle: "Choose the format you want to download.",
      options: [
        {
          key: "XLSX",
          title: "Excel (.xlsx) — recommended",
          hint: "One file with styled headers + multiple sheets (including Open ROs Now).",
          primary: true,
        },
        {
          key: "ZIP",
          title: "ZIP (CSV pack)",
          hint: "One ZIP containing multiple CSV files. Most compatible across systems.",
        },
      ],
    });
  } finally {
    exportChoiceBusy = false;
  }
}

async function chooseWipExportType() {
  if (exportChoiceBusy) return null;
  exportChoiceBusy = true;

  try {
    return await showExportChoice({
      title: "Export WIP (Filtered)",
      subtitle: "Choose the export format.",
      options: [
        {
          key: "XLSX",
          title: "Excel (.xlsx)",
          hint: "Styled header row + filter + frozen header.",
          primary: true,
        },
        {
          key: "CSV",
          title: "CSV (.csv)",
          hint: "Single CSV file (simple + universally supported).",
        },
      ],
    });
  } finally {
    exportChoiceBusy = false;
  }
}

export async function exportKpiRangeXlsx({
  filename,
  kpiRows = [],
  allRos = [],
  // (optional extras; safe to ignore if callers pass them)
  rangeLabel,
  stats,
} = {}) {
  // Infer from/to from filename like flowtrak_kpi_YYYY-MM-DD_to_YYYY-MM-DD.xlsx
  let fromDateKey = "";
  let toDateKey = "";

  if (typeof filename === "string") {
    const m = filename.match(/kpi_(\d{4}-\d{2}-\d{2})_to_(\d{4}-\d{2}-\d{2})/);
    if (m) {
      fromDateKey = m[1];
      toDateKey = m[2];
    }
  }

  const type = await chooseKpiExportType();
  if (!type) return;

  if (type === "ZIP") {
    await exportManagerReportZip({
      ros: kpiRows, // ZIP stays range-based (existing behavior)
      fromDateKey,
      toDateKey,
      filenamePrefix: "flowtrak_manager",
      // pass-through extras in case zip exporter wants them later
      rangeLabel,
      stats,
    });
    return;
  }

  await exportManagerReportXlsx({
    allRos,
    kpiRows,
    fromDateKey,
    toDateKey,
    filenamePrefix: "flowtrak_manager",
    rangeLabel,
    stats,
  });
}

export async function exportWipFilteredXlsx({
  dateLabel,
  wipRows = [],
  // (optional extras)
  counts,
} = {}) {
  const type = await chooseWipExportType();
  if (!type) return;

  if (type === "CSV") {
    exportWipCsv({
      ros: wipRows,
      dateKey: dateLabel,
      filters: {},
      filenamePrefix: "flowtrak_wip",
      counts, // optional
    });
    return;
  }

  await exportWipXlsx({
    wipRows,
    dateLabel,
    filenamePrefix: "flowtrak_wip",
    counts, // optional
  });
}

/**
 * ✅ Export Center helper (Dashboard)
 * selected: { KPI: true/false, WIP: true/false }
 *
 * This intentionally reuses your existing exporters + choice modals.
 * If user selects both, it will run exports sequentially.
 */
export async function exportDashboardSelected({
  selected,
  // KPI inputs:
  kpiFilename,
  kpiRows = [],
  allRos = [],
  rangeLabel,
  stats,
  // WIP inputs:
  wipDateLabel,
  wipRows = [],
  wipCounts,
} = {}) {
  const wantKpi = !!selected?.KPI;
  const wantWip = !!selected?.WIP;

  if (!wantKpi && !wantWip) return;

  // Run sequentially (prevents overlapping modals + keeps UX predictable)
  if (wantKpi) {
    await exportKpiRangeXlsx({
      filename: kpiFilename,
      kpiRows,
      allRos,
      rangeLabel,
      stats,
    });
  }

  if (wantWip) {
    await exportWipFilteredXlsx({
      dateLabel: wipDateLabel,
      wipRows,
      counts: wipCounts,
    });
  }
}
